import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../features/auth/AuthProvider.js";
import { ApiClientError } from "../services/api-client.js";
import * as traceabilityApi from "../services/traceability-api.js";
import type { TraceEvent, TraceEventType } from "../types/api.js";

function isPlatformRole(role: string): boolean {
  return role === "superadmin" || role === "platform-support";
}

const EVENT_TYPES: TraceEventType[] = [
  "product.created",
  "inventory.movement.created",
  "inventory.balance.adjusted",
  "inventory.reservation.created",
  "inventory.reservation.released",
  "order.created",
  "order.status.changed",
  "order.confirmation.failed",
];

function formatPayloadValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}

function PayloadView({ payload }: { payload: Record<string, unknown> | null }): JSX.Element {
  if (!payload || Object.keys(payload).length === 0) {
    return <p className="text-muted small">Sin payload adicional.</p>;
  }
  return (
    <div className="table-wrap">
      <table className="data-table payload-kv">
        <tbody>
          {Object.entries(payload).map(([k, v]) => (
            <tr key={k}>
              <th scope="row">
                <code>{k}</code>
              </th>
              <td className="payload-value">{formatPayloadValue(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TraceabilityPage(): JSX.Element {
  const { token, user } = useAuth();
  const [events, setEvents] = useState<TraceEvent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TraceEvent | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [filterEventType, setFilterEventType] = useState<TraceEventType | "">("");
  const [filterOrderId, setFilterOrderId] = useState("");
  const [filterProductId, setFilterProductId] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  const tenantQuery = useMemo(() => {
    if (user?.tenantId) return { tenantId: user.tenantId };
    return {};
  }, [user?.tenantId]);

  const loadList = useCallback(async () => {
    if (!token) return;
    setLoadState("loading");
    setErrorMsg(null);
    try {
      const parseLocal = (s: string): string | undefined => {
        if (!s.trim()) return undefined;
        const d = new Date(s);
        return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
      };
      const fromIso = parseLocal(filterFrom);
      const toIso = parseLocal(filterTo);
      const { events: list } = await traceabilityApi.fetchTraceEvents(token, {
        ...tenantQuery,
        eventType: filterEventType || undefined,
        orderId: filterOrderId.trim() || undefined,
        productId: filterProductId.trim() || undefined,
        from: fromIso,
        to: toIso,
      });
      setEvents(list);
      setLoadState("ok");
      setSelectedId((sid) => {
        if (sid && !list.some((e) => e.id === sid)) {
          setDetail(null);
          return null;
        }
        return sid;
      });
    } catch (e) {
      setLoadState("error");
      setErrorMsg(e instanceof ApiClientError ? e.message : "No se pudieron cargar los eventos.");
    }
  }, [token, tenantQuery, filterEventType, filterOrderId, filterProductId, filterFrom, filterTo]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (!token || !selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    void traceabilityApi
      .fetchTraceEventDetail(token, selectedId)
      .then((r) => {
        if (!cancelled) setDetail(r.event);
      })
      .catch((e) => {
        if (!cancelled) {
          setDetail(null);
          setErrorMsg(e instanceof ApiClientError ? e.message : "Error al cargar el detalle.");
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, selectedId]);

  return (
    <div className="inventory-page traceability-page">
      <header className="page-header">
        <h1>Trazabilidad</h1>
        <p className="text-muted">
          Eventos de <strong>negocio</strong> emitidos por la API (Fase 4.3): productos, movimientos, balances y pedidos. No
          sustituye auditoría legal ni blockchain.
        </p>
        {user && isPlatformRole(user.role) && !user.tenantId ? (
          <p className="banner-inline" role="note">
            Sesión de plataforma sin tenant: ve eventos de todos los tenants; use filtros para acotar.
          </p>
        ) : null}
      </header>

      <section className="card filters-card">
        <h2 className="section-title">Filtros</h2>
        <div className="filters-row trace-filters">
          <label className="field-inline">
            <span>Tipo de evento</span>
            <select value={filterEventType} onChange={(e) => setFilterEventType((e.target.value || "") as TraceEventType | "")}>
              <option value="">Todos</option>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="field-inline">
            <span>Order ID (UUID)</span>
            <input value={filterOrderId} onChange={(e) => setFilterOrderId(e.target.value)} placeholder="opcional" />
          </label>
          <label className="field-inline">
            <span>Product ID (UUID)</span>
            <input value={filterProductId} onChange={(e) => setFilterProductId(e.target.value)} placeholder="opcional" />
          </label>
          <label className="field-inline">
            <span>Desde</span>
            <input type="datetime-local" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
          </label>
          <label className="field-inline">
            <span>Hasta</span>
            <input type="datetime-local" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
          </label>
          <button type="button" className="btn btn-secondary" onClick={() => void loadList()} disabled={loadState === "loading"}>
            {loadState === "loading" ? "Cargando…" : "Aplicar"}
          </button>
        </div>
      </section>

      {loadState === "loading" && events.length === 0 ? <p className="text-muted">Cargando eventos…</p> : null}
      {loadState === "error" ? <p className="form-error">{errorMsg}</p> : null}

      {loadState === "ok" ? (
        <div className="orders-layout">
          <section className="card table-section">
            <h2 className="section-title">Eventos (máx. 500)</h2>
            {events.length === 0 ? (
              <p className="text-muted empty-hint">No hay eventos con los filtros actuales.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table orders-table">
                  <thead>
                    <tr>
                      <th>Cuándo</th>
                      <th>Evento</th>
                      <th>Entidad</th>
                      <th>Pedido</th>
                      <th>Producto</th>
                      <th>Actor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev) => (
                      <tr
                        key={ev.id}
                        className={selectedId === ev.id ? "row-selected" : undefined}
                        onClick={() => setSelectedId(ev.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedId(ev.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <td className="nowrap">{new Date(ev.occurredAt).toLocaleString()}</td>
                        <td>
                          <code className="small-code">{ev.eventType}</code>
                        </td>
                        <td>
                          <code>{ev.entityType}</code> <span className="text-muted">{ev.entityId.slice(0, 8)}…</span>
                        </td>
                        <td>{ev.orderId ? <code>{ev.orderId.slice(0, 8)}…</code> : "—"}</td>
                        <td>{ev.productId ? <code>{ev.productId.slice(0, 8)}…</code> : "—"}</td>
                        <td className="truncate">{ev.actorEmail ?? (ev.actorUserId ? `${ev.actorUserId.slice(0, 8)}…` : "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card table-section order-detail-card">
            <h2 className="section-title">Detalle</h2>
            {!selectedId ? (
              <p className="text-muted empty-hint">Seleccione un evento.</p>
            ) : detailLoading ? (
              <p className="text-muted">Cargando…</p>
            ) : detail ? (
              <>
                <dl className="order-meta">
                  <dt>ID</dt>
                  <dd>
                    <code>{detail.id}</code>
                  </dd>
                  <dt>Tipo</dt>
                  <dd>
                    <code>{detail.eventType}</code>
                  </dd>
                  <dt>Entidad</dt>
                  <dd>
                    <code>{detail.entityType}</code> — <code>{detail.entityId}</code>
                  </dd>
                  <dt>Pedido</dt>
                  <dd>{detail.orderId ? <code>{detail.orderId}</code> : "—"}</dd>
                  <dt>Producto</dt>
                  <dd>{detail.productId ? <code>{detail.productId}</code> : "—"}</dd>
                  <dt>Movimiento inv.</dt>
                  <dd>{detail.inventoryMovementId ? <code>{detail.inventoryMovementId}</code> : "—"}</dd>
                  <dt>Relacionado</dt>
                  <dd>
                    {detail.relatedEntityType ? (
                      <>
                        <code>{detail.relatedEntityType}</code> / <code>{detail.relatedEntityId ?? "—"}</code>
                      </>
                    ) : (
                      "—"
                    )}
                  </dd>
                  <dt>Actor</dt>
                  <dd>{detail.actorEmail ?? detail.actorUserId ?? "—"}</dd>
                  <dt>Ocurrió</dt>
                  <dd className="nowrap">{new Date(detail.occurredAt).toLocaleString()}</dd>
                </dl>
                <h3 className="subsection-title">Payload</h3>
                <PayloadView payload={detail.payload} />
              </>
            ) : (
              <p className="form-error">Sin detalle.</p>
            )}
          </section>
        </div>
      ) : null}

      <section className="card gaps-card">
        {/* Documentación oficial: https://github.com/jowy81/0xvera-trace-docs/blob/main/docs/00-overview/01-product.md#trazabilidad */}
        <h2 className="section-title">Alcance y límites</h2>
        <p className="text-muted small">
          <a
            href="https://github.com/jowy81/0xvera-trace-docs/blob/main/docs/00-overview/01-product.md#trazabilidad"
            target="_blank"
            rel="noreferrer"
          >
            Documentación oficial (trazabilidad)
          </a>
        </p>
      </section>
    </div>
  );
}
